import {DOCUMENT, Injectable, Injector, inject} from '@angular/core';
import {ConnectedPosition, Overlay, OverlayRef} from '@angular/cdk/overlay';
import {ComponentPortal} from '@angular/cdk/portal';
import {Rgba} from './color-format';
import {ColorPanelComponent} from './color-panel.component';
import {ColorPanelFormat, ColorPanelState, stateColor} from './color-panel-state';

/** How a panel closed: Choose, Enter or a click outside; Cancel; or Esc, another panel opening, or its opener going. */
export type ColorPanelCloseReason = 'choose' | 'cancel' | 'dismiss';

export interface ColorPanelResult {
  reason: ColorPanelCloseReason;
  /** The colour the close keeps: the panel's on a choose, else the one at opening. */
  state: ColorPanelState;
  /** The colour in the panel when it closed, whatever the reason. */
  last: ColorPanelState;
  /** The colour when it opened. */
  initial: ColorPanelState;
}

export interface ColorPanelConfig {
  state: ColorPanelState;
  alpha?: boolean;
  allowEmpty?: boolean;
  format?: ColorPanelFormat;
  recent?: string[];
  closeOnPalette?: boolean;
  /**
   * Puts the panel next to its origin in the page rather than at the end of it, so a container that hides when
   * the pointer leaves it (the page designer's block toolbar) still holds the panel.
   */
  inline?: boolean;
  /** Moves focus into the panel, for an opening from the keyboard. */
  focus?: boolean;
  moved?: (color: Rgba | null) => void;
  closed?: (result: ColorPanelResult) => void;
}

/**
 * Below the origin, its left edges aligned; above it when there is no room below. A panel that would run off the
 * screen's side is pushed back on. The CDK checks the right edge against the screen less both 8px margins, so a
 * panel at its widest (the screen less 32px) ends up 16px from either side.
 */
const POSITIONS: ConnectedPosition[] = [
  {originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top'},
  {originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom'},
];

export class ColorPanelRef {
  private done = false;
  /** Focus was moved in from the keyboard, so it goes back to the origin when the panel closes. */
  private keyboard = false;
  private readonly removeListeners: () => void;

  constructor(
    private readonly overlayRef: OverlayRef,
    readonly panel: ColorPanelComponent,
    readonly origin: HTMLElement,
    private readonly initial: ColorPanelState,
    private readonly config: ColorPanelConfig,
    private readonly document: Document,
    onClose: () => void,
  ) {
    // Spectrum's rules: a click anywhere else chooses, Esc closes. Clicks inside the panel stop at the panel.
    const click = (event: MouseEvent) => {
      if (!panel.dragging && !origin.contains(event.target as Node)) {
        this.close('choose');
      }
    };
    const keydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        this.close('dismiss');
      }
    };
    document.addEventListener('click', click);
    document.addEventListener('keydown', keydown);
    this.removeListeners = () => {
      document.removeEventListener('click', click);
      document.removeEventListener('keydown', keydown);
      onClose();
    };
  }

  get isOpen(): boolean {
    return !this.done;
  }

  /** Moves focus into the panel: the palette's swatch of the colour, else its first. */
  focus(): void {
    this.keyboard = true;
    this.panel.focusFirst();
  }

  /** The colour in the panel now. */
  get color(): Rgba | null {
    return stateColor(this.panel.state(), this.config.allowEmpty ?? true);
  }

  close(reason: ColorPanelCloseReason = 'dismiss'): void {
    if (this.done) {
      return;
    }
    this.done = true;
    const last = this.panel.state();
    const state = reason === 'choose' ? last : this.initial;
    // Keyboard focus in the panel goes back to the field that opened it, rather than to the page's start. A tap does
    // not, as it would bring up a phone's keyboard.
    const hadFocus = this.keyboard && this.overlayRef.overlayElement.contains(this.document.activeElement);
    this.removeListeners();
    this.overlayRef.dispose();
    if (hadFocus) {
      this.origin.focus();
    }
    this.config.closed?.({reason, state, last, initial: this.initial});
  }

  updatePosition(): void {
    this.overlayRef.updatePosition();
  }
}

/** Opens the colour panel under an element; one panel is open at a time, as with Spectrum. */
@Injectable({providedIn: 'root'})
export class ColorPanelService {
  private readonly overlay = inject(Overlay);
  private readonly injector = inject(Injector);
  private readonly document = inject(DOCUMENT);
  private current: ColorPanelRef | null = null;

  open(origin: HTMLElement, config: ColorPanelConfig): ColorPanelRef {
    this.current?.close('dismiss');
    const position = this.overlay.position()
      .flexibleConnectedTo(origin)
      .withPositions(POSITIONS)
      .withPush(true)
      .withViewportMargin(8)
      .withPopoverLocation(config.inline ? 'inline' : 'global');
    const overlayRef = this.overlay.create({
      positionStrategy: position,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      panelClass: 'color-panel-pane',
    });
    const componentRef = overlayRef.attach(new ComponentPortal(ColorPanelComponent, null, this.injector));
    const panel = componentRef.instance;
    componentRef.setInput('state', config.state);
    componentRef.setInput('alpha', !!config.alpha);
    componentRef.setInput('allowEmpty', config.allowEmpty ?? true);
    componentRef.setInput('format', config.format || 'hex');
    componentRef.setInput('recent', config.recent || []);
    componentRef.setInput('closeOnPalette', !!config.closeOnPalette);
    componentRef.changeDetectorRef.detectChanges();
    overlayRef.updatePosition();

    const ref = new ColorPanelRef(overlayRef, panel, origin, config.state, config, this.document, () => {
      if (this.current === ref) {
        this.current = null;
      }
    });
    this.current = ref;
    panel.moved.subscribe((color) => config.moved?.(color));
    panel.chosen.subscribe(() => ref.close('choose'));
    panel.cancelled.subscribe(() => ref.close('cancel'));
    overlayRef.detachments().subscribe(() => ref.close('dismiss'));
    if (config.focus) {
      ref.focus();
    }
    return ref;
  }
}
